import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type TimerStatus = 'idle' | 'running' | 'paused' | 'complete';
export type ProgressMode = 'relative' | 'proportional';
export type SoundscapeType = 'none' | 'white-noise' | 'rain' | 'lo-fi';

interface TimerState {
  duration: number;
  remaining: number;
  status: TimerStatus;
  // For smooth countdown while running
  lastTick: number | null;
  // Wall-clock start time for persistence across page navigation (Date.now() when timer started)
  startTime: number | null;
  // Tracks if user has manually set a custom duration
  customDuration: boolean;
  // Progress display mode: 'relative' drains to 0%, 'proportional' drains to 60-min reference
  progressMode: ProgressMode;
  // Alarm sound duration in seconds (3, 5, 10, or 15)
  alarmDuration: number;
  // Currently selected ambient soundscape
  soundscape: SoundscapeType;
}

const initialState: TimerState = {
  duration: 3600, // 60 minutes default
  remaining: 3600,
  status: 'idle',
  lastTick: null,
  startTime: null,
  customDuration: false,
  progressMode: 'proportional',
  alarmDuration: 5, // Default 5 seconds alarm duration
  soundscape: 'none',
};

const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    setDuration: (state, action: PayloadAction<number>) => {
      const newDuration = Math.max(60, action.payload);
      state.duration = newDuration;
      state.remaining = newDuration;
      state.status = state.status === 'running' ? 'running' : 'idle';
      state.lastTick = state.status === 'running' ? Date.now() : null;
      state.customDuration = true;
    },

    setDurationWhileStopped: (state, action: PayloadAction<number>) => {
      // Only allowed when timer is not running
      if (state.status !== 'idle' && state.status !== 'complete' && state.status !== 'paused') return;
      const newDuration = Math.max(60, action.payload);
      state.duration = newDuration;
      state.remaining = newDuration;
      state.status = 'idle';
      state.lastTick = null;
      state.customDuration = true;
    },

    startTimer: (state) => {
      if (state.status === 'idle' || state.status === 'complete') {
        state.duration = state.remaining;
      }
      state.status = 'running';
      state.lastTick = Date.now();
      // Store wall-clock start time for persistence across page navigation
      state.startTime = Date.now() - ((state.duration - state.remaining) * 1000);
    },

    pauseTimer: (state) => {
      if (state.status === 'running') {
        // Calculate elapsed since last tick
        const elapsed = Math.floor((Date.now() - state.lastTick!) / 1000);
        state.remaining = Math.max(state.remaining - elapsed, 0);
        state.lastTick = null;
      }
      state.status = 'paused';
    },

    resumeTimer: (state) => {
      if (state.status === 'paused') {
        state.status = 'running';
        state.lastTick = Date.now();
      }
    },

    stopTimer: (state) => {
      state.status = 'idle';
      state.remaining = state.duration;
      state.lastTick = null;
      state.startTime = null;
    },

    resetTimer: (state) => {
      state.status = 'idle';
      state.remaining = state.duration;
      state.lastTick = null;
      state.startTime = null;
    },

    setSoundscape: (state, action: PayloadAction<SoundscapeType>) => {
      state.soundscape = action.payload;
    },

    tickTimer: (state) => {
      if (state.status === 'running' && state.lastTick) {
        const now = Date.now();
        const elapsed = (now - state.lastTick) / 1000; // fractional seconds
        state.lastTick = now;
        state.remaining = Math.max(state.remaining - elapsed, 0);

        // Check for completion
        if (state.remaining <= 0) {
          state.status = 'complete';
          state.lastTick = null;
          state.remaining = 0;
        }
      }
    },

    setRemaining: (state, action: PayloadAction<number>) => {
      state.remaining = Math.max(0, action.payload);
      if (state.remaining <= 0) {
        state.status = 'complete';
        state.lastTick = null;
      }
    },

    toggleProgressMode: (state) => {
      state.progressMode = state.progressMode === 'relative' ? 'proportional' : 'relative';
    },

    setProgressMode: (state, action: PayloadAction<ProgressMode>) => {
      state.progressMode = action.payload;
    },

    setAlarmDuration: (state, action: PayloadAction<number>) => {
      const validDurations = [3, 5, 10, 15];
      if (!validDurations.includes(action.payload)) return;
      state.alarmDuration = action.payload;
    },
  },
});

export const {
  setDuration,
  setDurationWhileStopped,
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  resetTimer,
  tickTimer,
  setRemaining,
  toggleProgressMode,
  setProgressMode,
  setAlarmDuration,
  setSoundscape,
} = timerSlice.actions;

export default timerSlice.reducer;

// Selectors
export const selectTimerDuration = (state: { timer: TimerState }) => state.timer.duration;
export const selectTimerRemaining = (state: { timer: TimerState }) => state.timer.remaining;
export const selectTimerStatus = (state: { timer: TimerState }) => state.timer.status;
export const selectTimerProgressMode = (state: { timer: TimerState }) => state.timer.progressMode;
export const selectTimerAlarmDuration = (state: { timer: TimerState }) => state.timer.alarmDuration;
export const selectTimerSoundscape = (state: { timer: TimerState }) => state.timer.soundscape;

// Helper: Calculate progress for two-draining-rings display
// Returns { outerProgress, innerProgress } where:
// - outerProgress: remaining time scaled to 60 minutes (proportional) or remaining hours / total hours (relative)
// - innerProgress: remaining minutes in current hour (proportional) or remaining minutes / total minutes (relative)
export const selectTimerRingProgress = (state: { timer: TimerState }) => {
  const timer = state.timer;
  if (!timer || timer.duration <= 0) return { outerProgress: 0, innerProgress: 0 };

  const { duration, remaining, progressMode } = timer;
  const totalMinutes = duration / 60;
  const remainingMinutes = remaining / 60;
  const totalHours = duration / 3600;
  const remainingHours = remaining / 3600;

  if (progressMode === 'proportional') {
    // Proportional mode: outer ring = 23 hour sections (hours 1-23), inner ring = granular 0-60 min view
    // The inner ring ALWAYS shows remaining minutes (0-60) as a granular view
    // It fills first (when setting timer) and drains last (when timer is almost complete)
    // Outer ring: shows remaining hours as fraction of 23 sections (hours 1-23)
    const remainingHours = Math.floor(remaining / 3600);
    const outerProgress = Math.max(0, Math.min(1, remainingHours / 23));
    // Inner ring: shows remaining minutes in current hour (0-60) - always active
    const minutesIntoHour = (remaining % 3600) / 60;
    const innerProgress = Math.max(0, Math.min(1, minutesIntoHour / 60));
    return { outerProgress, innerProgress };
  }

  // Relative mode: relative to selected duration
  // Outer ring: remaining hours / total hours
  const outerProgress = Math.max(0, Math.min(1, remainingHours / totalHours));
  // Inner ring: remaining minutes / total minutes
  const innerProgress = Math.max(0, Math.min(1, remainingMinutes / totalMinutes));
  return { outerProgress, innerProgress };
};
